/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Loader2, type LucideIcon } from 'lucide-react';
import { Icon } from './Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive';
export type ButtonSize = 'sm' | 'md';

/** The class list for a button-looking element (also used by links that look like buttons). */
export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', iconOnly = false, extra?: string): string {
  return ['ui-btn', `ui-btn--${variant}`, size === 'sm' ? 'ui-btn--sm' : '', iconOnly ? 'ui-btn--icon' : '', extra ?? ''].filter(Boolean).join(' ');
}

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon. */
  icon?: LucideIcon;
  /** Trailing icon (an arrow on a link, a chevron on a menu). */
  iconAfter?: LucideIcon;
  children?: ReactNode;
}

export interface ButtonProps extends CommonProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Shows a spinner in place of the icon and disables the button while an action runs. */
  busy?: boolean;
  /** The button element (React 19 passes a ref as a prop; it reaches the element with the rest). */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * One button for the console: at most one `primary` per page header, `secondary` for the rest, `ghost` inside toolbars
 * and rows, `danger` for actions that remove something (always behind a ConfirmDialog, whose confirm button is the solid
 * `destructive`). An icon-only button must carry an `aria-label`.
 */
export function Button({ variant = 'secondary', size = 'md', icon, iconAfter, busy = false, children, className, type = 'button', disabled, ...rest }: ButtonProps) {
  const iconOnly = !children;
  const iconSize = size === 'sm' ? 'sm' : 'md';
  return (
    <button type={type} className={buttonClass(variant, size, iconOnly, className)} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {busy ? <Icon icon={Loader2} size={iconSize} className="ui-spin" /> : icon ? <Icon icon={icon} size={iconSize} /> : null}
      {children !== undefined && children !== null && children !== false ? <span className="ui-btn-label">{children}</span> : null}
      {iconAfter ? <Icon icon={iconAfter} size={iconSize} /> : null}
    </button>
  );
}

export interface LinkButtonProps extends CommonProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'children'> {
  href: string;
}

/** A link that looks like a button (navigation, never an action). */
export function LinkButton({ variant = 'secondary', size = 'md', icon, iconAfter, children, className, ...rest }: LinkButtonProps) {
  const iconSize = size === 'sm' ? 'sm' : 'md';
  return (
    <a className={buttonClass(variant, size, !children, className)} {...rest}>
      {icon ? <Icon icon={icon} size={iconSize} /> : null}
      {children ? <span className="ui-btn-label">{children}</span> : null}
      {iconAfter ? <Icon icon={iconAfter} size={iconSize} /> : null}
    </a>
  );
}
